import React, { useEffect, useRef, useState } from 'react';
import { MdDelete, MdSearch } from 'react-icons/md';
import { HiOutlineUsers, HiOutlineExclamation } from 'react-icons/hi';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAtom } from 'jotai';
import { useDebounce } from 'usehooks-ts';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';
import { Button } from '../../tailwindComponents/Button';
import { PageHeader } from '../../tailwindComponents/PageHeader';
import { useAuthHttp } from '../../hooks';
import {
  DELETE_WAREHOUSE_MATCH,
  GET_MATCH_SIGNING_DETAILS,
  MATCH_LIST,
} from '../../constants';
import { notificationsAtom } from '../../store/jotai';
import {
  ACTIVE_MATCH_STATUSES,
  splitCustomersByType,
} from './customerCategorisation';

type CustomerType = 'current' | 'former';
type StatusFilter = 'all' | 'Negotiating' | 'Reviewing T&Cs' | 'Contracted';

const PAGE_SIZE = 500;
const MAX_MATCHES = 5000;

const ROW_ACTION_LABELS: Record<string, string> = {
  'Not engaged': 'View request',
  Negotiating: 'Review rates',
  'Reviewing T&Cs': 'Sign contract',
  Contracted: 'View contract',
};

dayjs.extend(relativeTime);

type SortKey =
  | 'customer'
  | 'requestDate'
  | 'industry'
  | 'storage'
  | 'startDate'
  | 'duration'
  | 'inbound'
  | 'outbound'
  | 'status';

type SortDirection = 'asc' | 'desc';

type MatchRow = {
  match: {
    userBusinessName?: string;
    createdAt?: string;
    shipperIndustry?: string;
    averageStorageQuantityPerWeek?: number;
    desiredStartDate?: string;
    estimatedDurationInMonths?: number;
    averageNumberOfInboundShipmentsPerWeek?: number;
    averageNumberOfOutboundShipmentsPerWeek?: number;
    matchStatus?: string;
  };
};

type MatchListResponse = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  matches?: any[];
  lastItemSort?: object[] | null;
  totalCount?: number;
};

const SORT_COLUMNS: Record<
  SortKey,
  {
    type: 'text' | 'number' | 'date';
    getValue: (row: MatchRow) => string | number;
  }
> = {
  customer: {
    type: 'text',
    getValue: (row) => row.match.userBusinessName ?? '',
  },
  requestDate: { type: 'date', getValue: (row) => row.match.createdAt ?? '' },
  industry: {
    type: 'text',
    getValue: (row) => row.match.shipperIndustry ?? '',
  },
  storage: {
    type: 'number',
    getValue: (row) => row.match.averageStorageQuantityPerWeek ?? 0,
  },
  startDate: {
    type: 'date',
    getValue: (row) => row.match.desiredStartDate ?? '',
  },
  duration: {
    type: 'number',
    getValue: (row) => row.match.estimatedDurationInMonths ?? 0,
  },
  inbound: {
    type: 'number',
    getValue: (row) => row.match.averageNumberOfInboundShipmentsPerWeek ?? 0,
  },
  outbound: {
    type: 'number',
    getValue: (row) => row.match.averageNumberOfOutboundShipmentsPerWeek ?? 0,
  },
  status: { type: 'text', getValue: (row) => row.match.matchStatus ?? '' },
};

const formatRequestDate = (value?: string) => {
  const date = dayjs(value);
  return value && date.isValid() ? date.format('D MMM YYYY') : '—';
};

const formatRequestAge = (value?: string) => {
  const date = dayjs(value);
  return value && date.isValid() ? date.fromNow() : '';
};

export function CustomerShortlist() {
  const [notifications] = useAtom(notificationsAtom);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [customers, setCustomers] = useState<any[]>([]);
  const [isCustomersLoading, setIsCustomersLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [customerTypeFilter, setCustomerTypeFilter] =
    useState<CustomerType>('current');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('requestDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [matchesSigningDetails, setMatchesSigningDetails] = useState<
    {
      isSigned: boolean;
      signedAt: string;
      signedByEmail: string;
      signingUrl: string;
      matchId?: string;
    }[]
  >([]);
  const authHttp = useAuthHttp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const focusedMatchId = searchParams.get('matchId');
  const [highlightedMatchId, setHighlightedMatchId] = useState<string | null>(
    null
  );
  const hasFocusedMatchFromQuery = useRef(false);

  const loadAllCustomerShortlist = async (
    search: string,
    status: StatusFilter
  ) => {
    setIsCustomersLoading(true);
    try {
      // Only filter by a specific status when one is selected; otherwise fetch
      // the active statuses explicitly — a null filter makes the backend
      // default the LSP list to one that includes Deleted and Rejected.
      const matchStatuses: string[] =
        status !== 'all' ? [status] : ACTIVE_MATCH_STATUSES;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const allMatches: any[] = [];
      let cursor: object[] | null = null;
      let total = 0;

      // Match/List caps every response at 500 rows and is seek-paginated, so
      // page through the cursor until the whole set is loaded — the client-side
      // sort below must cover every request, not just the first page.
      do {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, no-await-in-loop
        const response: any = await authHttp.post(
          MATCH_LIST,
          {
            searchQuery: search || null,
            matchStatuses,
            pagination: {
              lastItemSort: cursor,
              itemsPerPage: PAGE_SIZE,
            },
          },
          { headers: { 'Content-Type': 'application/json' } }
        );
        const customerShortlist: MatchListResponse = response.data;

        const mappedMatches =
          customerShortlist.matches?.map(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (match: any) => ({
              ...match,
              matchId: match.match.id,
              isSignedByShipper:
                match.match.legalDocuments[0]?.isSignedByShipper,
            })
          ) ?? [];

        allMatches.push(...mappedMatches);
        cursor = customerShortlist.lastItemSort ?? null;
        total = customerShortlist.totalCount ?? allMatches.length;
      } while (
        cursor &&
        allMatches.length < total &&
        allMatches.length < MAX_MATCHES
      );

      setCustomers(allMatches);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsCustomersLoading(false);
    }
  };

  // Fetch when search or status filter changes (not tab)
  useEffect(() => {
    loadAllCustomerShortlist(debouncedSearchQuery, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery, statusFilter]);

  const { current: currentCustomers, former: formerCustomers } =
    splitCustomersByType(customers);

  const filteredCustomers =
    customerTypeFilter === 'former' ? formerCustomers : currentCustomers;

  useEffect(() => {
    if (
      hasFocusedMatchFromQuery.current ||
      !focusedMatchId ||
      isCustomersLoading
    ) {
      return;
    }
    const focusedCustomer = customers.find(
      (customer) => customer.matchId === focusedMatchId
    );
    if (!focusedCustomer) return;
    hasFocusedMatchFromQuery.current = true;
    setCustomerTypeFilter(
      formerCustomers.includes(focusedCustomer) ? 'former' : 'current'
    );
    setExpandedRows((rows) => new Set(rows).add(focusedMatchId));
    setHighlightedMatchId(focusedMatchId);
    setTimeout(() => setHighlightedMatchId(null), 5000);
    requestAnimationFrame(() => {
      document
        .getElementById(`customer-row-${focusedMatchId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, isCustomersLoading]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((direction) => (direction === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDirection(SORT_COLUMNS[key].type === 'text' ? 'asc' : 'desc');
  };

  const sortedCustomers = [...filteredCustomers].sort((a, b) => {
    const column = SORT_COLUMNS[sortKey];
    let comparison: number;
    if (column.type === 'number') {
      comparison =
        (column.getValue(a) as number) - (column.getValue(b) as number);
    } else if (column.type === 'date') {
      const aTime = dayjs(column.getValue(a) as string).valueOf();
      const bTime = dayjs(column.getValue(b) as string).valueOf();
      comparison =
        (Number.isNaN(aTime) ? 0 : aTime) - (Number.isNaN(bTime) ? 0 : bTime);
    } else {
      comparison = String(column.getValue(a)).localeCompare(
        String(column.getValue(b)),
        undefined,
        { sensitivity: 'base' }
      );
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  // Calculate counts from fetched data for tab badges
  const currentCount = currentCustomers.length;
  const formerCount = formerCustomers.length;

  useEffect(() => {
    const notification = notifications?.[0];
    if (
      notification &&
      [
        'WarehouseMatch.Accepted',
        'WarehouseMatch.Proposed',
        'WarehouseMatch.Created',
        'WarehouseMatch.Deleted',
        'WarehouseMatch.DocumentUpdated',
      ].includes(notification.notificationType)
    ) {
      loadAllCustomerShortlist(debouncedSearchQuery, statusFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications]);

  const handleFetchMatchSigningDetails = async (matchId: string) => {
    try {
      const { data: signingDetails } = await authHttp.get(
        GET_MATCH_SIGNING_DETAILS,
        {
          params: {
            matchId,
          },
        }
      );

      if (signingDetails) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setMatchesSigningDetails((prev: any) => [
          ...prev,
          { matchId, ...signingDetails },
        ]);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsCustomersLoading(false);
    }
  };

  const handleDeleteMatch = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    matchId: string
  ) => {
    e.stopPropagation();
    setIsCustomersLoading(true);
    try {
      const { data: matchDeleted } = await authHttp.post(
        DELETE_WAREHOUSE_MATCH,
        {
          match: {
            matchId,
          },
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (matchDeleted.match) {
        loadAllCustomerShortlist(debouncedSearchQuery, statusFilter);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsCustomersLoading(false);
    }
  };

  useEffect(() => {
    customers.forEach((contract) => {
      if (contract.isSignedByShipper) {
        handleFetchMatchSigningDetails(contract.matchId);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers]);

  const getStatusBadge = (status: string, isActionRequired: boolean) => {
    const statusColors: Record<string, string> = {
      'Not engaged':
        'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-700/10',
      Negotiating:
        'bg-yellow-50 text-yellow-800 ring-1 ring-inset ring-yellow-600/20',
      'Reviewing T&Cs':
        'bg-purple-50 text-purple-700 ring-1 ring-inset ring-purple-700/10',
      Contracted:
        'bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20',
    };

    return (
      <span className='inline-flex items-center gap-1'>
        <span
          className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
            statusColors[status] ||
            'bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-500/10'
          }`}
        >
          {status === 'Not engaged' ? 'Non-engaged' : status}
        </span>
        {isActionRequired && status === 'Negotiating' && (
          <HiOutlineExclamation className='w-4 h-4 text-yellow-500' />
        )}
      </span>
    );
  };

  // All statuses available for filtering (backend query)
  const allStatuses = ['Negotiating', 'Reviewing T&Cs', 'Contracted'] as const;

  const pages = [
    { name: 'Home', href: '/', current: false },
    {
      name: 'Customer Requests & Contracts',
      href: '/customer-shortlist',
      current: true,
    },
  ];

  const renderSortableHeader = (
    key: SortKey,
    label: string,
    align: 'left' | 'center' = 'left'
  ) => {
    const isActive = sortKey === key;
    const alignClass = align === 'center' ? 'text-center' : 'text-left';
    let ariaSort: 'ascending' | 'descending' | undefined;
    if (isActive) {
      ariaSort = sortDirection === 'asc' ? 'ascending' : 'descending';
    }
    return (
      <th
        aria-sort={ariaSort}
        className={`px-4 py-3 ${alignClass} text-xs font-medium text-gray-500 uppercase tracking-wider`}
      >
        <button
          type='button'
          onClick={() => handleSort(key)}
          className={`group inline-flex items-center gap-1 hover:text-gray-700 ${
            align === 'center' ? 'w-full justify-center' : ''
          } ${isActive ? 'text-gray-700' : ''}`}
        >
          {label}
          <ChevronDownIcon
            className={`h-3.5 w-3.5 shrink-0 transition-transform ${
              isActive
                ? `text-gray-700 ${sortDirection === 'asc' ? 'rotate-180' : ''}`
                : 'text-gray-300 group-hover:text-gray-400'
            }`}
          />
        </button>
      </th>
    );
  };

  return (
    <>
      <PageHeader header='Customer Requests & Contracts' pages={pages} />
      <main className='flex flex-col gap-4 min-h-[calc(100vh-4rem)] lg:min-h-[calc(100vh-5rem)] p-4 lg:pt-4 lg:px-8 lg:pb-8 relative'>
        <div className='bg-white rounded-lg border border-gray-200 overflow-hidden'>
          {/* Customer Type Tabs */}
          <div className='border-b border-gray-200'>
            <nav className='flex -mb-px'>
              <button
                type='button'
                onClick={() => setCustomerTypeFilter('current')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  customerTypeFilter === 'current'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Current Customers
                <span
                  className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                    customerTypeFilter === 'current'
                      ? 'bg-blue-100 text-blue-600'
                      : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {currentCount}
                </span>
              </button>
              <button
                type='button'
                onClick={() => setCustomerTypeFilter('former')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  customerTypeFilter === 'former'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Former Customers
                <span
                  className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                    customerTypeFilter === 'former'
                      ? 'bg-blue-100 text-blue-600'
                      : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {formerCount}
                </span>
              </button>
            </nav>
          </div>

          {/* Header with search and filters */}
          <div className='flex items-center gap-4 p-4 border-b border-gray-200'>
            {/* Search input */}
            <div className='relative flex-1 max-w-sm'>
              <input
                type='text'
                placeholder='Customer, warehouse or keyword...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
              />
              <MdSearch className='absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400' />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className='px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white'
            >
              <option value='all'>All Statuses</option>
              {allStatuses.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>

          {/* Table */}
          <div className='overflow-x-auto'>
            <table className='w-full'>
              <thead>
                <tr className='border-b border-gray-200 bg-white'>
                  <th className='w-10 px-2 py-3'>
                    <span className='sr-only'>Expand</span>
                  </th>
                  {renderSortableHeader('customer', 'Customer')}
                  {renderSortableHeader('requestDate', 'Request Date')}
                  {renderSortableHeader('industry', 'Industry')}
                  {renderSortableHeader('storage', 'Storage')}
                  {renderSortableHeader('startDate', 'Start Date')}
                  {renderSortableHeader('duration', 'Duration')}
                  {renderSortableHeader('inbound', 'Inbound')}
                  {renderSortableHeader('outbound', 'Outbound')}
                  {renderSortableHeader('status', 'Status', 'center')}
                  <th className='w-12 px-4 py-3'>
                    <span className='sr-only'>Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-200'>
                {isCustomersLoading && (
                  <tr>
                    <td colSpan={11} className='text-center py-12'>
                      <div className='flex flex-col items-center gap-3'>
                        <FloxSpinner size='md' />
                        <p className='text-gray-500 text-sm'>
                          Loading customers...
                        </p>
                      </div>
                    </td>
                  </tr>
                )}

                {!isCustomersLoading && filteredCustomers.length === 0 && (
                  <tr>
                    <td colSpan={11} className='text-center py-12'>
                      <div className='flex flex-col items-center gap-3'>
                        <HiOutlineUsers className='w-12 h-12 text-gray-300' />
                        <p className='text-gray-900 font-medium'>
                          {customerTypeFilter === 'former'
                            ? 'No former customers found'
                            : 'No customer requests found'}
                        </p>
                        <p className='text-gray-500 text-sm'>
                          {customerTypeFilter === 'former'
                            ? 'Contracted customers will appear here after their contracts end'
                            : 'Customer requests will appear here when shippers express interest in your warehouses'}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}

                {!isCustomersLoading &&
                  sortedCustomers.map((customer) => {
                    const rowKey = customer.matchId;
                    const setRowOpen = (
                      e: React.MouseEvent<HTMLButtonElement, MouseEvent>
                    ) => {
                      e.stopPropagation();
                      const newExpandedRows = new Set(expandedRows);
                      if (expandedRows.has(rowKey)) {
                        newExpandedRows.delete(rowKey);
                      } else {
                        newExpandedRows.add(rowKey);
                      }
                      setExpandedRows(newExpandedRows);
                    };

                    const isExpanded = expandedRows.has(rowKey);
                    const hasRequirements =
                      customer.match.requiredAccreditations ||
                      customer.match.sustainabilityRequirements ||
                      customer.match.temperatureRequirements ||
                      customer.match.productType ||
                      customer.match.productDetailRequirements ||
                      customer.match.approxNumberOfSkus ||
                      customer.match.averageNumberOfOrderlines ||
                      customer.match.additionalServices;

                    return (
                      <React.Fragment key={customer.matchId}>
                        <tr
                          id={`customer-row-${rowKey}`}
                          className={`hover:bg-gray-50 cursor-pointer ${
                            rowKey === highlightedMatchId ? 'bg-blue-50' : ''
                          }`}
                          onClick={() => {
                            navigate(
                              `/warehouse/${customer.match.warehouseId}/manager-service-rates?matchId=${customer.match.id}`
                            );
                          }}
                        >
                          {/* Expand */}
                          <td className='px-2 py-4'>
                            {hasRequirements && (
                              <button
                                type='button'
                                onClick={setRowOpen}
                                className='p-1 rounded hover:bg-gray-100 transition-colors'
                              >
                                <ChevronDownIcon
                                  className={`h-4 w-4 text-gray-400 transition-transform ${
                                    isExpanded ? 'rotate-180' : ''
                                  }`}
                                />
                              </button>
                            )}
                          </td>
                          {/* Customer */}
                          <td className='px-4 py-4'>
                            <div className='flex flex-col'>
                              <span className='text-sm font-medium text-gray-900'>
                                {customer.match.userBusinessName}
                              </span>
                              <span className='text-sm text-gray-500'>
                                {customer.match.warehouseName}
                              </span>
                            </div>
                          </td>

                          {/* Request Date */}
                          <td className='px-4 py-4'>
                            <div className='flex flex-col'>
                              <span className='text-sm text-gray-900'>
                                {formatRequestDate(customer.match.createdAt)}
                              </span>
                              <span className='text-xs text-gray-500'>
                                {formatRequestAge(customer.match.createdAt)}
                              </span>
                            </div>
                          </td>

                          {/* Industry */}
                          <td className='px-4 py-4'>
                            <span className='text-sm text-gray-900'>
                              {customer.match.shipperIndustry}
                            </span>
                          </td>

                          {/* Storage */}
                          <td className='px-4 py-4'>
                            <div className='flex flex-col'>
                              <span className='text-sm font-medium text-gray-900'>
                                {customer.match.averageStorageQuantityPerWeek}
                              </span>
                              <span className='text-xs text-gray-500'>
                                {
                                  customer.warehouse
                                    .storageCapacityUnitOfMeasure
                                }
                              </span>
                            </div>
                          </td>

                          {/* Start Date */}
                          <td className='px-4 py-4'>
                            <span className='text-sm text-gray-900'>
                              {customer.match.desiredStartDate}
                            </span>
                          </td>

                          {/* Duration */}
                          <td className='px-4 py-4'>
                            <div className='flex flex-col'>
                              <span className='text-sm font-medium text-gray-900'>
                                {customer.match.estimatedDurationInMonths}
                              </span>
                              <span className='text-xs text-gray-500'>
                                months
                              </span>
                            </div>
                          </td>

                          {/* Inbound */}
                          <td className='px-4 py-4'>
                            <div className='flex flex-col'>
                              <span className='text-sm font-medium text-gray-900'>
                                {
                                  customer.match
                                    .averageNumberOfInboundShipmentsPerWeek
                                }
                              </span>
                              <span className='text-xs text-gray-500'>
                                {
                                  customer.warehouse
                                    .storageCapacityUnitOfMeasure
                                }
                              </span>
                            </div>
                          </td>

                          {/* Outbound */}
                          <td className='px-4 py-4'>
                            <div className='flex flex-col'>
                              <span className='text-sm font-medium text-gray-900'>
                                {
                                  customer.match
                                    .averageNumberOfOutboundShipmentsPerWeek
                                }
                              </span>
                              <span className='text-xs text-gray-500'>
                                {
                                  customer.warehouse
                                    .storageCapacityUnitOfMeasure
                                }
                              </span>
                            </div>
                          </td>

                          {/* Status */}
                          <td className='px-4 py-4 text-center'>
                            {customer.match.matchStatus === 'Reviewing T&Cs' &&
                            customer.isSignedByShipper
                              ? (() => {
                                  const signingDetail =
                                    matchesSigningDetails.find(
                                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                      (detail: any) =>
                                        detail.matchId === customer.matchId
                                    );
                                  return signingDetail &&
                                    signingDetail.signingUrl ? (
                                    <a
                                      href={signingDetail.signingUrl}
                                      target='_blank'
                                      rel='noopener noreferrer'
                                      className='inline-flex items-center rounded-full px-3 py-1 text-xs font-medium bg-purple-50 text-purple-700 ring-1 ring-inset ring-purple-700/10 hover:bg-purple-100'
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      Review and sign
                                    </a>
                                  ) : (
                                    getStatusBadge(
                                      customer.match.matchStatus,
                                      customer.match.isActionRequiredByLsp
                                    )
                                  );
                                })()
                              : getStatusBadge(
                                  customer.match.matchStatus,
                                  customer.match.isActionRequiredByLsp
                                )}
                          </td>

                          {/* Actions */}
                          <td className='px-4 py-4'>
                            <div className='flex items-center justify-end gap-2'>
                              <Button
                                label={
                                  ROW_ACTION_LABELS[
                                    customer.match.matchStatus
                                  ] ?? 'View'
                                }
                                intent='secondary'
                                size='xs'
                                className='h-auto whitespace-nowrap'
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(
                                    `/warehouse/${customer.match.warehouseId}/manager-service-rates?matchId=${customer.match.id}`
                                  );
                                }}
                              />
                              {(customer.match.matchStatus === 'Not engaged' ||
                                customer.match.matchStatus ===
                                  'Negotiating') && (
                                <button
                                  type='button'
                                  onClick={(e) =>
                                    handleDeleteMatch(e, customer.match.id)
                                  }
                                  className='p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors'
                                >
                                  <MdDelete className='w-5 h-5' />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {isExpanded && hasRequirements && (
                          <tr className='bg-gray-50'>
                            <td colSpan={11} className='px-6 py-4'>
                              <div className='grid grid-cols-2 gap-x-8 gap-y-3 sm:grid-cols-4'>
                                {customer.match.requiredAccreditations && (
                                  <div>
                                    <dt className='text-xs font-medium text-gray-500'>
                                      Required Accreditations
                                    </dt>
                                    <dd className='mt-0.5 text-sm text-gray-900'>
                                      {customer.match.requiredAccreditations}
                                    </dd>
                                  </div>
                                )}
                                {customer.match.sustainabilityRequirements && (
                                  <div>
                                    <dt className='text-xs font-medium text-gray-500'>
                                      Sustainability Requirements
                                    </dt>
                                    <dd className='mt-0.5 text-sm text-gray-900'>
                                      {
                                        customer.match
                                          .sustainabilityRequirements
                                      }
                                    </dd>
                                  </div>
                                )}
                                {customer.match.temperatureRequirements && (
                                  <div>
                                    <dt className='text-xs font-medium text-gray-500'>
                                      Temperature Requirements
                                    </dt>
                                    <dd className='mt-0.5 text-sm text-gray-900'>
                                      {customer.match.temperatureRequirements}
                                    </dd>
                                  </div>
                                )}
                                {customer.match.productType && (
                                  <div>
                                    <dt className='text-xs font-medium text-gray-500'>
                                      Product Type
                                    </dt>
                                    <dd className='mt-0.5 text-sm text-gray-900'>
                                      {customer.match.productType}
                                    </dd>
                                  </div>
                                )}
                                {customer.match.productDetailRequirements && (
                                  <div>
                                    <dt className='text-xs font-medium text-gray-500'>
                                      Product Detail Requirements
                                    </dt>
                                    <dd className='mt-0.5 text-sm text-gray-900'>
                                      {customer.match.productDetailRequirements}
                                    </dd>
                                  </div>
                                )}
                                {customer.match.approxNumberOfSkus != null && (
                                  <div>
                                    <dt className='text-xs font-medium text-gray-500'>
                                      Approx. SKUs
                                    </dt>
                                    <dd className='mt-0.5 text-sm text-gray-900'>
                                      {customer.match.approxNumberOfSkus}
                                    </dd>
                                  </div>
                                )}
                                {customer.match.averageNumberOfOrderlines !=
                                  null && (
                                  <div>
                                    <dt className='text-xs font-medium text-gray-500'>
                                      Avg. Orderlines
                                    </dt>
                                    <dd className='mt-0.5 text-sm text-gray-900'>
                                      {customer.match.averageNumberOfOrderlines}
                                    </dd>
                                  </div>
                                )}
                                {customer.match.additionalServices && (
                                  <div>
                                    <dt className='text-xs font-medium text-gray-500'>
                                      Additional Services
                                    </dt>
                                    <dd className='mt-0.5 text-sm text-gray-900'>
                                      {customer.match.additionalServices}
                                    </dd>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filteredCustomers.length > 0 && (
            <div className='flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50'>
              <div className='text-sm text-gray-500'>
                Showing{' '}
                <span className='font-medium'>{filteredCustomers.length}</span>{' '}
                {customerTypeFilter === 'current' ? 'current' : 'former'}{' '}
                customers
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
}
